import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Sparkles,
  Users,
  User,
  Globe,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Info,
  X,
  Search,
  Copy,
  Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Skills cache helpers (localStorage, 30 min TTL, user-scoped)
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const getSkillsCacheKey = (userId) => `verbagpt_skills_cache_${userId}`;

const getCachedSkills = (userId) => {
  try {
    const cached = localStorage.getItem(getSkillsCacheKey(userId));
    if (cached) {
      const { skills, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL) return skills;
    }
  } catch (e) {
    console.warn('Skills cache read error:', e);
  }
  return null;
};

const setCachedSkills = (userId, skills) => {
  try {
    localStorage.setItem(getSkillsCacheKey(userId), JSON.stringify({
      skills,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn('Skills cache write error:', e);
  }
};

const clearSkillsCache = (userId) => {
  try {
    localStorage.removeItem(getSkillsCacheKey(userId));
  } catch (e) {
    console.warn('Skills cache clear error:', e);
  }
};

/**
 * SkillsPanel - Manages Claude SDK skills for Taurus mode
 *
 * Admin/Owner view: Org-level skills management
 * User view: Personal skills + forking org skills
 */
// Format relative time (e.g., "3 days ago")
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

function SkillsPanel({ user, authToken, showNotification }) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSkill, setExpandedSkill] = useState(null);
  const [editingSkill, setEditingSkill] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isCustomizing, setIsCustomizing] = useState(false);  // True when forking an org skill
  const [customizeSourceId, setCustomizeSourceId] = useState(null);  // Source skill ID when customizing
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  // Filter skills based on search query
  const filteredSkills = useMemo(() => {
    if (!searchQuery.trim()) return skills;
    const query = searchQuery.toLowerCase();
    return skills.filter(s =>
      s.name.toLowerCase().includes(query) ||
      s.description?.toLowerCase().includes(query) ||
      s.content?.toLowerCase().includes(query)
    );
  }, [skills, searchQuery]);

  // Group skills by name for nested display (personal + org source together)
  const groupedSkills = useMemo(() => {
    const groups = {};
    filteredSkills.forEach(skill => {
      if (!groups[skill.name]) {
        groups[skill.name] = { personal: null, org: null, demo: null };
      }
      // Categorize by source
      if (skill.source === 'personal' || skill.share_level === 'private') {
        groups[skill.name].personal = skill;
      } else if (skill.source === 'org' || skill.share_level === 'org') {
        groups[skill.name].org = skill;
      } else if (skill.source === 'demo' || skill.share_level === 'demo') {
        groups[skill.name].demo = skill;
      }
    });

    // Convert to array with computed properties
    return Object.entries(groups).map(([name, versions]) => ({
      name,
      // Active skill is the one that will sync to Claude (personal > org > demo)
      active: versions.personal || versions.org || versions.demo,
      personal: versions.personal,
      // Source skill is the org/demo skill that was potentially forked
      orgSource: versions.personal ? (versions.org || versions.demo) : null,
      // For skills without personal override, org or demo is the source
      originalSkill: !versions.personal ? (versions.org || versions.demo) : null
    }));
  }, [filteredSkills]);

  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'admin' || isOwner;
  const isIndividualUser = !user?.org_id;  // User not part of any org

  // For individual users, they manage their own skills via /skills endpoint (not /skills/me)
  // Backend handles this by creating personal skills when org_id is null
  const canManageSkills = isAdmin || isIndividualUser;

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    share_level: 'private'  // 'private', 'org', 'demo' (demo = owner only)
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch skills (with cache support)
  const fetchSkills = useCallback(async (forceRefresh = false) => {
    // Check cache first (unless force refresh)
    if (!forceRefresh && user?.id) {
      const cached = getCachedSkills(user.id);
      if (cached) {
        setSkills(cached);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // Admins and individual users use /skills, org users use /skills/me
      const endpoint = canManageSkills ? '/skills' : '/skills/me';
      const response = await fetch(`${API}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch skills');
      }

      const fetchedSkills = data.skills || [];
      setSkills(fetchedSkills);

      // Cache the result
      if (user?.id) {
        setCachedSkills(user.id, fetchedSkills);
      }
    } catch (err) {
      console.error('Error fetching skills:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken, canManageSkills, user?.id]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.length > 64) {
      errors.name = 'Name must be 64 characters or less';
    } else if (!/^[a-z0-9-\s_]+$/i.test(formData.name)) {
      errors.name = 'Name can only contain letters, numbers, hyphens, and spaces';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.length > 1024) {
      errors.description = 'Description must be 1024 characters or less';
    }

    if (!formData.content.trim()) {
      errors.content = 'Content is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Create skill (or fork when customizing)
  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      // If customizing (forking), use fork endpoint
      if (isCustomizing && customizeSourceId) {
        await forkSkill(
          customizeSourceId,
          true,  // enabled
          formData.content,
          formData.description
        );
        showNotification('success', `Created your customized version of "${formData.name}"`);
      } else {
        // Normal create
        const endpoint = canManageSkills ? '/skills' : '/skills/me';
        const response = await fetch(`${API}${endpoint}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to create skill');
        }

        showNotification('success', 'Skill created successfully');
      }

      setIsCreating(false);
      setIsCustomizing(false);
      setCustomizeSourceId(null);
      setFormData({ name: '', description: '', content: '', share_level: 'private' });
      clearSkillsCache(user?.id);
      fetchSkills(true);
    } catch (err) {
      showNotification('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  // Update skill
  const handleUpdate = async () => {
    if (!validateForm() || !editingSkill) return;

    try {
      setSaving(true);
      const endpoint = canManageSkills
        ? `/skills/${editingSkill.id}`
        : `/skills/me/${editingSkill.id}`;

      const response = await fetch(`${API}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update skill');
      }

      showNotification('success', 'Skill updated successfully');
      setEditingSkill(null);
      setFormData({ name: '', description: '', content: '', share_level: 'private' });
      clearSkillsCache(user?.id);
      fetchSkills(true);
    } catch (err) {
      showNotification('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete skill
  const handleDelete = async (skill) => {
    if (!window.confirm(`Delete skill "${skill.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const endpoint = canManageSkills
        ? `/skills/${skill.id}`
        : `/skills/me/${skill.id}`;

      const response = await fetch(`${API}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete skill');
      }

      showNotification('success', 'Skill deleted');
      clearSkillsCache(user?.id);
      fetchSkills(true);
    } catch (err) {
      showNotification('error', err.message);
    }
  };

  // Fork an org/demo skill to create a personal override
  const forkSkill = async (skillId, isEnabled, content = null, description = null) => {
    try {
      const response = await fetch(`${API}/skills/me/fork/${skillId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_enabled: isEnabled,
          content: content,
          description: description
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to fork skill');
      }

      return await response.json();
    } catch (err) {
      throw err;
    }
  };

  // Toggle skill enabled/disabled
  const handleToggle = async (skill) => {
    try {
      setToggling(skill.id);

      // If it's a personal skill, toggle directly
      if (skill.share_level === 'private' || skill.source === 'personal') {
        const endpoint = canManageSkills
          ? `/skills/${skill.id}`
          : `/skills/me/${skill.id}`;

        const response = await fetch(`${API}${endpoint}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_enabled: !skill.is_enabled })
        });

        if (!response.ok) {
          throw new Error('Failed to toggle skill');
        }
      } else {
        // For org/demo skills, fork to create a personal disabled copy
        await forkSkill(skill.id, !skill.is_enabled);
        showNotification('success', skill.is_enabled
          ? `Disabled "${skill.name}" for your account`
          : `Enabled "${skill.name}" for your account`);
      }

      clearSkillsCache(user?.id);
      fetchSkills(true);
    } catch (err) {
      showNotification('error', err.message);
    } finally {
      setToggling(null);
    }
  };

  // Start editing
  const startEdit = (skill) => {
    setEditingSkill(skill);
    setFormData({
      name: skill.name,
      description: skill.description,
      content: skill.content,
      share_level: skill.share_level || 'private'
    });
    setFormErrors({});
    setIsCreating(false);
    setIsCustomizing(false);
    setCustomizeSourceId(null);
  };

  // Start customizing (forking) an org/demo skill
  const startCustomize = (skill) => {
    setIsCustomizing(true);
    setCustomizeSourceId(skill.id);
    setFormData({
      name: skill.name,  // Name is locked when customizing
      description: skill.description,
      content: skill.content,
      share_level: 'private'  // Always personal when customizing
    });
    setFormErrors({});
    setEditingSkill(null);
    setIsCreating(true);  // Reuse create modal
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingSkill(null);
    setIsCreating(false);
    setIsCustomizing(false);
    setCustomizeSourceId(null);
    setFormData({ name: '', description: '', content: '', share_level: 'private' });
    setFormErrors({});
  };

  // Render skill modal
  const renderModal = () => {
    if (!isCreating && !editingSkill) return null;

    const modalTitle = editingSkill
      ? 'Edit Skill'
      : isCustomizing
        ? 'Customize Skill'
        : 'Create New Skill';

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {modalTitle}
            </h3>
            <button
              onClick={cancelEdit}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-4 space-y-3">
            {/* Customize info banner */}
            {isCustomizing && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p className="font-medium">Creating your personal version</p>
                  <p className="text-xs mt-0.5 opacity-80">
                    Your customized skill will override the shared version. The original remains available to others.
                  </p>
                </div>
              </div>
            )}

            {/* Name + Share Level on same row */}
            <div className="flex gap-3">
              {/* Name - takes more space */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </label>
                  {!isCustomizing && (
                    <span className={`text-xs ${formData.name.length > 54 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {formData.name.length}/64
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., analyzing-sales-data"
                  maxLength={64}
                  readOnly={isCustomizing}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    isCustomizing
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white'
                  } ${
                    formErrors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {formErrors.name && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
                )}
              </div>

              {/* Share Level - fixed width (hidden when customizing) */}
              {!isCustomizing && (
                <div className="w-44">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    Share Level
                  </label>
                  <select
                    value={formData.share_level}
                    onChange={(e) => setFormData(prev => ({ ...prev, share_level: e.target.value }))}
                    className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="private">Private</option>
                    {user?.org_id && isAdmin && <option value="org">Organization</option>}
                    {isOwner && <option value="demo">Demo (all users)</option>}
                  </select>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <span className={`text-xs ${formData.description.length > 900 ? 'text-amber-500' : 'text-gray-400'}`}>
                  {formData.description.length}/1024
                </span>
              </div>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Analyze sales performance, revenue trends, and customer metrics. Use when working with sales reports, CRM data, or revenue forecasting."
                maxLength={1024}
                rows={2}
                className={`w-full bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none ${
                  formErrors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Include trigger keywords and "Use when..." scenarios. Claude matches queries to skills using this description.
              </p>
              {formErrors.description && (
                <p className="text-xs text-red-500 mt-1">{formErrors.description}</p>
              )}
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Skill Content (Markdown)
                </label>
                <span className={`text-xs ${(formData.content.split('\n').length) > 400 ? 'text-amber-500' : 'text-gray-400'}`}>
                  {formData.content.split('\n').length} lines
                </span>
              </div>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="# Instructions for Claude&#10;&#10;When analyzing sales data:&#10;1. First check for null values...&#10;2. Calculate key metrics..."
                rows={10}
                className={`w-full bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  formErrors.content ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {formErrors.content && (
                <p className="text-xs text-red-500 mt-1">{formErrors.content}</p>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={cancelEdit}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={editingSkill ? handleUpdate : handleCreate}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingSkill ? 'Update Skill' : isCustomizing ? 'Save My Version' : 'Create Skill'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Get badge for share level
  const getShareLevelBadge = (skill) => {
    const level = skill.share_level || (skill.is_org_skill ? 'org' : 'private');
    switch (level) {
      case 'demo':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded text-xs">
            <Globe className="w-3 h-3" />
            Demo
          </span>
        );
      case 'org':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded text-xs">
            <Users className="w-3 h-3" />
            Org
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs">
            <User className="w-3 h-3" />
            Personal
          </span>
        );
    }
  };

  // Render skill card
  const renderSkillCard = (skill, options = {}) => {
    const { isSource = false, isNested = false } = options;
    const isExpanded = expandedSkill === skill.id;
    const isPersonal = skill.share_level === 'private' || skill.source === 'personal';
    const isOrgOrDemo = skill.share_level === 'org' || skill.share_level === 'demo';

    // Can edit: admins/owners can edit org/demo skills, users can edit their personal skills
    const canEdit = (canManageSkills && (skill.share_level !== 'demo' || isOwner)) || isPersonal;

    // Can customize: org users can customize shared skills (creates personal fork)
    const canCustomize = !canManageSkills && isOrgOrDemo && !isSource;

    return (
      <div
        key={skill.id}
        className={`border rounded-lg overflow-hidden transition-all ${
          isSource
            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 opacity-80'
            : skill.is_enabled
              ? 'border-purple-200 dark:border-purple-800 bg-white dark:bg-gray-800'
              : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 opacity-75'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Toggle - now works for all skills (forks on toggle for org/demo) */}
            {!isSource ? (
              <button
                onClick={() => handleToggle(skill)}
                disabled={toggling === skill.id}
                className="flex-shrink-0"
                title={skill.is_enabled ? 'Disable skill' : 'Enable skill'}
              >
                {toggling === skill.id ? (
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                ) : skill.is_enabled ? (
                  <ToggleRight className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-gray-400" />
                )}
              </button>
            ) : (
              <div className="flex-shrink-0 w-6" /> // Spacer for alignment
            )}

            {/* Name & Badge */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-medium truncate ${
                  isSource
                    ? 'text-gray-500 dark:text-gray-400'
                    : skill.is_enabled
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                }`}>
                  vg-{skill.name}
                </span>
                {!isNested && getShareLevelBadge(skill)}
                {skill.is_customized && (
                  <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded text-xs">
                    customized
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                {skill.description}
              </p>
              {/* Show who shared the skill for org/demo skills */}
              {isOrgOrDemo && skill.created_by_email && !isNested && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  Shared by {skill.created_by_email}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 ml-2">
            {/* Customize button for org/demo skills */}
            {canCustomize && (
              <button
                onClick={() => startCustomize(skill)}
                className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded transition-colors"
                title="Customize this skill"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}

            {canEdit && (
              <>
                <button
                  onClick={() => startEdit(skill)}
                  className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Edit skill"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(skill)}
                  className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 rounded transition-colors"
                  title={isPersonal && skill.is_customized ? 'Delete (restores org version)' : 'Delete skill'}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}

            <button
              onClick={() => setExpandedSkill(isExpanded ? null : skill.id)}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
            <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono overflow-x-auto max-h-64 overflow-y-auto">
              {skill.content}
            </pre>
            {skill.created_by_email && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Created by: {skill.created_by_email}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render a skill group (active skill + nested source if applicable)
  const renderSkillGroup = (group) => {
    const { personal, orgSource, originalSkill, active } = group;

    // If no personal override, just render the original org/demo skill
    if (!personal && originalSkill) {
      return (
        <div key={group.name} className="space-y-1">
          {renderSkillCard(originalSkill)}
        </div>
      );
    }

    // If personal override exists, show it with nested source
    if (personal) {
      // Check if source was updated after the fork
      const sourceUpdated = orgSource &&
        orgSource.updated_at && personal.created_at &&
        new Date(orgSource.updated_at) > new Date(personal.created_at);

      return (
        <div key={group.name} className="space-y-1">
          {renderSkillCard(personal)}

          {/* Nested source skill */}
          {orgSource && (
            <div className="ml-4 pl-3 border-l-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span>Original from {orgSource.created_by_email || 'admin'}</span>
                {sourceUpdated && (
                  <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <Clock className="w-3 h-3" />
                    Updated {formatRelativeTime(orgSource.updated_at)}
                  </span>
                )}
              </div>
              {renderSkillCard(orgSource, { isSource: true, isNested: true })}
            </div>
          )}
        </div>
      );
    }

    // Fallback: just render active skill
    return (
      <div key={group.name} className="space-y-1">
        {active && renderSkillCard(active)}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading skills...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load skills: {error}</span>
        </div>
        <button
          onClick={fetchSkills}
          className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            Skills
            <div className="relative">
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="p-1 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="How skills work"
              >
                <Info className="w-4 h-4" />
              </button>
              {showHelp && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowHelp(false)} />
                  <div className="absolute left-0 top-8 z-20 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
                    <p className="font-medium text-sm text-gray-900 dark:text-white mb-2">How Skills Work</p>
                    <ul className="text-xs space-y-1.5 text-gray-600 dark:text-gray-300">
                      <li>• Skills sync to Claude when you start a Taurus session</li>
                      <li>• Claude auto-invokes skills based on your queries</li>
                      <li>• Disabled skills are not synced</li>
                      <li>• Personal skills override org/demo skills with same name</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {isIndividualUser
              ? 'Create personal skills that Claude will use in Taurus mode.'
              : isAdmin
                ? 'Create skills for your organization. Members can customize these for their own use.'
                : 'Your personal skills and organization skills available to you.'}
          </p>
        </div>
        {!isCreating && !editingSkill && (
          <button
            onClick={() => {
              setIsCreating(true);
              setFormData({ name: '', description: '', content: '', share_level: isOwner ? 'demo' : (isAdmin && user?.org_id ? 'org' : 'private') });
              setFormErrors({});
            }}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Skill
          </button>
        )}
      </div>

      {/* Search */}
      {skills.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-20 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          {searchQuery && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {groupedSkills.length} found
              </span>
              <button
                onClick={() => setSearchQuery('')}
                className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Skills List */}
      {skills.length > 0 ? (
        groupedSkills.length > 0 ? (
          <div className="space-y-2">
            {groupedSkills.map(renderSkillGroup)}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600 dark:text-gray-400 text-sm">No skills match "{searchQuery}"</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-2 text-sm text-purple-600 dark:text-purple-400 hover:underline"
            >
              Clear search
            </button>
          </div>
        )
      ) : (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <Sparkles className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">No skills yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            {isOwner
              ? 'Create demo skills to share with all users, or personal skills for your own use.'
              : isAdmin
                ? 'Create org skills for your team, or personal skills for yourself.'
                : 'Create personal skills to teach Claude domain-specific knowledge.'}
          </p>
        </div>
      )}

      {/* Modal - rendered last for proper z-index */}
      {renderModal()}
    </div>
  );
}

export default SkillsPanel;
